import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Megaphone, Newspaper, BookOpen, ClipboardList, Trash2, Pencil, Save, Loader2 } from "lucide-react";
import ChurchBrand from "@/components/ChurchBrand";

const PASTOR_KEY = "pastor_token";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; }
}

// ---------------- Login ----------------
function PastorLogin({ onAuth }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/pastor/login", { password });
      localStorage.setItem(PASTOR_KEY, data.token);
      onAuth(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Contraseña incorrecta");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="bg-white border-2 border-[#D1C9C1] rounded-2xl p-8 max-w-md w-full">
        <Link to="/" className="inline-flex items-center gap-2 text-[#8A3A33] hover:underline mb-5 text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
        <ChurchBrand size="md" className="mb-6 text-[#1A1818]" />
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-full bg-[#2C5545]/10 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-[#2C5545]" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-[#1A1818]">Panel del Pastor</h1>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-base font-semibold mb-1.5 block">Contraseña</Label>
            <Input
              data-testid="pastor-password"
              type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 text-base border-2 border-[#D1C9C1]" autoFocus
            />
          </div>
          <Button
            data-testid="pastor-login-submit"
            type="submit" disabled={loading}
            className="w-full h-12 text-base font-semibold bg-[#2C5545] hover:bg-[#1F3D32] text-white"
          >
            {loading ? "Verificando..." : "Entrar"}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ---------------- Form fields shared ----------------
function FormShell({ children, onCancel, onSubmit, saving, title }) {
  return (
    <div className="bg-white border-2 border-[#D1C9C1] rounded-2xl p-6 sm:p-8">
      <h2 className="font-serif text-2xl font-bold text-[#1A1818] mb-5">{title}</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        {children}
        <div className="flex flex-col sm:flex-row gap-3 pt-3">
          <Button
            type="button" variant="outline" onClick={onCancel}
            className="h-14 text-base border-2 border-[#D1C9C1] flex-1"
          >
            Cancelar
          </Button>
          <Button
            type="submit" disabled={saving}
            data-testid="form-publish"
            className="h-14 text-lg font-bold bg-[#2C5545] hover:bg-[#1F3D32] text-white flex-1"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? "PUBLICANDO..." : "PUBLICAR"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ---------------- Devotional form ----------------
function DevotionalForm({ token, initial, onDone, onCancel }) {
  const [f, setF] = useState(initial || {
    title: "", scripture: "", message: "", pastor_name: "",
    publish_at: todayISO(), featured: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.scripture || !f.message || !f.pastor_name || !f.publish_at) {
      toast.error("Por favor complete todos los campos");
      return;
    }
    setSaving(true);
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      if (initial?.id) await api.put(`/devotionals/${initial.id}`, f, cfg);
      else await api.post("/devotionals", f, cfg);
      toast.success("Mensaje publicado");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al publicar");
    } finally { setSaving(false); }
  };

  return (
    <FormShell onCancel={onCancel} onSubmit={submit} saving={saving} title="🟩 Nuevo Devocional">
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Título del mensaje *</Label>
        <Input data-testid="dev-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
      </div>
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Referencia bíblica *</Label>
        <Input data-testid="dev-scripture" value={f.scripture} onChange={(e) => setF({ ...f, scripture: e.target.value })} placeholder="Ej. Proverbios 3:5-6" className="h-12 text-base border-2 border-[#D1C9C1]" />
      </div>
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Mensaje *</Label>
        <Textarea data-testid="dev-message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={6} className="text-base border-2 border-[#D1C9C1] min-h-[150px]" />
      </div>
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Nombre del Pastor *</Label>
        <Input data-testid="dev-pastor" value={f.pastor_name} onChange={(e) => setF({ ...f, pastor_name: e.target.value })} placeholder="Ej. Juan Pérez" className="h-12 text-base border-2 border-[#D1C9C1]" />
      </div>
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Fecha de publicación *</Label>
        <Input data-testid="dev-date" type="date" value={f.publish_at} onChange={(e) => setF({ ...f, publish_at: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
        <p className="text-sm text-[#4A4646] mt-1">Si elige una fecha futura, se publicará automáticamente ese día.</p>
      </div>
      <div className="flex items-center gap-3 bg-[#FEF3C7] border-2 border-[#D97706]/30 rounded-lg p-3">
        <Checkbox id="featured" data-testid="dev-featured" checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: !!v })} />
        <Label htmlFor="featured" className="text-base font-medium cursor-pointer">Destacar en la página principal</Label>
      </div>
    </FormShell>
  );
}

// ---------------- Bulletin form ----------------
function BulletinForm({ token, initial, onDone, onCancel }) {
  const [f, setF] = useState(initial || { title: "", content: "", author_name: "", publish_at: todayISO() });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.content || !f.author_name) {
      toast.error("Por favor complete todos los campos");
      return;
    }
    setSaving(true);
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      if (initial?.id) await api.put(`/bulletins/${initial.id}`, f, cfg);
      else await api.post("/bulletins", f, cfg);
      toast.success("Boletín publicado");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <FormShell onCancel={onCancel} onSubmit={submit} saving={saving} title="🟦 Nuevo Boletín">
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Título *</Label>
        <Input data-testid="bul-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
      </div>
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Contenido *</Label>
        <Textarea data-testid="bul-content" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} rows={6} className="text-base border-2 border-[#D1C9C1] min-h-[150px]" />
      </div>
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Nombre del autor *</Label>
        <Input data-testid="bul-author" value={f.author_name} onChange={(e) => setF({ ...f, author_name: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
      </div>
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Fecha de publicación *</Label>
        <Input data-testid="bul-date" type="date" value={f.publish_at} onChange={(e) => setF({ ...f, publish_at: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
      </div>
    </FormShell>
  );
}

// ---------------- Announcement form ----------------
function AnnouncementForm({ token, initial, onDone, onCancel }) {
  const [f, setF] = useState(initial || { title: "", description: "", event_date: "", location: "", author_name: "", publish_at: todayISO() });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.description || !f.author_name) { toast.error("Por favor complete los campos requeridos"); return; }
    setSaving(true);
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      if (initial?.id) await api.put(`/announcements/${initial.id}`, f, cfg);
      else await api.post("/announcements", f, cfg);
      toast.success("Anuncio publicado");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <FormShell onCancel={onCancel} onSubmit={submit} saving={saving} title="🟪 Nuevo Anuncio">
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Título *</Label>
        <Input data-testid="ann-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
      </div>
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Descripción *</Label>
        <Textarea data-testid="ann-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={5} className="text-base border-2 border-[#D1C9C1]" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-base font-semibold mb-1.5 block">Fecha del evento</Label>
          <Input data-testid="ann-event-date" type="date" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
        </div>
        <div>
          <Label className="text-base font-semibold mb-1.5 block">Ubicación</Label>
          <Input data-testid="ann-location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Opcional" className="h-12 text-base border-2 border-[#D1C9C1]" />
        </div>
      </div>
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Nombre del autor *</Label>
        <Input data-testid="ann-author" value={f.author_name} onChange={(e) => setF({ ...f, author_name: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
      </div>
      <div>
        <Label className="text-base font-semibold mb-1.5 block">Fecha de publicación *</Label>
        <Input data-testid="ann-publish-date" type="date" value={f.publish_at} onChange={(e) => setF({ ...f, publish_at: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
      </div>
    </FormShell>
  );
}

// ---------------- Publications list ----------------
function PublicationsList({ token, onEdit, refreshKey }) {
  const [items, setItems] = useState({ devotionals: [], bulletins: [], announcements: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const [d, b, a] = await Promise.all([
        api.get("/devotionals", cfg),
        api.get("/bulletins/all", cfg),
        api.get("/announcements/all", cfg),
      ]);
      setItems({ devotionals: d.data, bulletins: b.data, announcements: a.data });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const remove = async (kind, id) => {
    try {
      await api.delete(`/${kind}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const Section = ({ kind, label, list }) => (
    <div className="mb-6">
      <h3 className="font-serif text-xl font-bold text-[#1A1818] mb-3">{label} ({list.length})</h3>
      {list.length === 0 ? (
        <p className="text-base text-[#4A4646] italic">Sin publicaciones.</p>
      ) : (
        <div className="space-y-3">
          {list.map((it) => (
            <div key={it.id} data-testid={`pub-${kind}-${it.id}`} className="bg-white border-2 border-[#D1C9C1] rounded-xl p-4 flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-serif font-bold text-[#1A1818] text-base">{it.title}</p>
                <p className="text-sm text-[#4A4646] mt-1">{formatDate(it.publish_at)} · {it.author_role === "pastor" ? "Pastor" : "Admin"}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => onEdit(kind, it)} className="border-2 border-[#D1C9C1]" data-testid={`pub-edit-${it.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="border-2 border-red-300 text-red-700 hover:bg-red-50" data-testid={`pub-delete-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar publicación?</AlertDialogTitle>
                      <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(kind, it.id)} className="bg-red-700 hover:bg-red-800" data-testid={`pub-confirm-delete-${it.id}`}>Eliminar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) return <Loader2 className="w-8 h-8 animate-spin text-[#8A3A33] mx-auto my-10" />;

  return (
    <div className="bg-white border-2 border-[#D1C9C1] rounded-2xl p-6">
      <h2 className="font-serif text-2xl font-bold text-[#1A1818] mb-5">🟧 Ver Publicaciones</h2>
      <Section kind="devotionals" label="Devocionales" list={items.devotionals} />
      <Section kind="bulletins" label="Boletines" list={items.bulletins} />
      <Section kind="announcements" label="Anuncios" list={items.announcements} />
    </div>
  );
}

// ---------------- Big Button ----------------
function BigButton({ color, label, icon: Icon, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`${color} hover:brightness-110 active:brightness-95 text-white rounded-2xl p-6 sm:p-8 shadow-md transition-all flex flex-col items-center justify-center gap-3 min-h-[160px] focus-visible:outline-4`}
    >
      <Icon className="w-12 h-12" strokeWidth={1.6} />
      <span className="font-serif text-lg sm:text-xl font-bold leading-tight text-center">{label}</span>
    </button>
  );
}

// ---------------- Main Page ----------------
export default function PastorPage() {
  const [token, setToken] = useState(localStorage.getItem(PASTOR_KEY) || "");
  const [view, setView] = useState("dashboard"); // dashboard | newDev | newBul | newAnn | list | edit
  const [editing, setEditing] = useState(null); // { kind, item }
  const [refreshKey, setRefreshKey] = useState(0);

  const logout = () => {
    localStorage.removeItem(PASTOR_KEY);
    setToken("");
  };

  if (!token) return <PastorLogin onAuth={setToken} />;

  const goDash = () => { setView("dashboard"); setEditing(null); setRefreshKey((k) => k + 1); };

  return (
    <div className="min-h-screen bg-paper">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-4">
          <Link to="/" className="inline-flex items-center gap-2 text-[#8A3A33] hover:underline text-base font-medium">
            <ArrowLeft className="w-5 h-5" /> Inicio
          </Link>
          <Button data-testid="pastor-logout" onClick={logout} variant="outline" className="border-2 border-[#D1C9C1] h-11">
            <LogOut className="w-4 h-4 mr-2" /> Salir
          </Button>
        </div>

        <ChurchBrand size="md" className="mb-2 text-[#1A1818]" />
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A1818] text-center mb-2">
          Panel del Pastor
        </h1>
        <p className="text-base text-[#4A4646] text-center mb-7">
          Comparta la palabra con la familia de Dios.
        </p>

        {view === "dashboard" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <BigButton testid="btn-new-bulletin" color="bg-[#3B7AB8]" label="🟦 NUEVO BOLETÍN" icon={Newspaper} onClick={() => setView("newBul")} />
            <BigButton testid="btn-new-devotional" color="bg-[#2C5545]" label="🟩 NUEVO DEVOCIONAL" icon={BookOpen} onClick={() => setView("newDev")} />
            <BigButton testid="btn-new-announcement" color="bg-[#8A3A33]" label="🟪 NUEVO ANUNCIO" icon={Megaphone} onClick={() => setView("newAnn")} />
            <BigButton testid="btn-view-publications" color="bg-[#D97706]" label="🟧 VER PUBLICACIONES" icon={ClipboardList} onClick={() => setView("list")} />
          </div>
        )}

        {view === "newDev" && <DevotionalForm token={token} onDone={goDash} onCancel={goDash} />}
        {view === "newBul" && <BulletinForm token={token} onDone={goDash} onCancel={goDash} />}
        {view === "newAnn" && <AnnouncementForm token={token} onDone={goDash} onCancel={goDash} />}

        {view === "list" && (
          <PublicationsList
            token={token}
            refreshKey={refreshKey}
            onEdit={(kind, item) => { setEditing({ kind, item }); setView("edit"); }}
          />
        )}

        {view === "edit" && editing && (
          <>
            {editing.kind === "devotionals" && <DevotionalForm token={token} initial={editing.item} onDone={goDash} onCancel={goDash} />}
            {editing.kind === "bulletins" && <BulletinForm token={token} initial={editing.item} onDone={goDash} onCancel={goDash} />}
            {editing.kind === "announcements" && <AnnouncementForm token={token} initial={editing.item} onDone={goDash} onCancel={goDash} />}
          </>
        )}
      </div>
    </div>
  );
}
